<template>
  <div class="container-fluid">

    <div class="page-title">
      <div class="row">
        <div class="col-12"><h3>Diagnóstico de reverdecimiento</h3></div>
        <div class="col-6"><ol class="breadcrumb"></ol></div>
      </div>
    </div>

    <div class="row starter-main">
      <div class="col-sm-12 col-lg-8 offset-lg-2">

        <!-- Banner evaluación previa -->
        <div v-if="evaluacionPrevia" class="card mb-3 quiz-previa">
          <div class="card-body d-flex align-items-center gap-4 flex-wrap py-3">
            <div class="flex-grow-1">
              <p class="text-muted small mb-1">Tu evaluación actual</p>
              <h5 class="mb-0 fw-bold" style="color:#09377c;">{{ evaluacionPrevia.nivel }}</h5>
            </div>
            <div class="text-center px-2">
              <p class="text-muted small mb-1">Puntaje</p>
              <span class="badge bg-primary fs-6">{{ evaluacionPrevia.total }} pts</span>
            </div>
            <div style="min-width:180px; flex-grow:1;">
              <p class="text-muted small mb-1">Progreso</p>
              <div class="progress" style="height:10px;">
                <div class="progress-bar bg-success" :style="{ width: Math.min(100, evaluacionPrevia.total) + '%' }"></div>
              </div>
            </div>
            <div class="text-muted small">Folio&nbsp;<strong>#{{ evaluacionPrevia.respuesta_id }}</strong></div>
          </div>
        </div>

        <!-- Cargando -->
        <div v-if="loading" class="card">
          <div class="card-body text-center py-5">
            <div class="loader-box"><div class="loader-3"></div></div>
            <p class="mt-2 text-muted small">Cargando cuestionario…</p>
          </div>
        </div>

        <!-- Error de carga -->
        <div v-else-if="errorCarga" class="card">
          <div class="card-body">
            <div class="alert alert-danger mb-0">
              {{ errorCarga }}
              <button class="btn btn-sm btn-link p-0 ms-2" @click="init">Reintentar</button>
            </div>
          </div>
        </div>

        <!-- ═══════════  PREGUNTA ACTIVA  ═══════════ -->
        <div v-else-if="!enRevision && preguntaActual" class="card quiz-card">

          <!-- Cabecera con progreso -->
          <div class="card-header quiz-card__header">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="quiz-card__step">
                Pregunta <strong>{{ currentIndex + 1 }}</strong> de {{ preguntas.length }}
              </span>
              <span class="quiz-card__answered">
                <i class="fa fa-check-circle me-1"></i>{{ respondidas }} respondidas
              </span>
            </div>
            <div class="quiz-progress">
              <div class="quiz-progress__bar" :style="{ width: progreso + '%' }"></div>
            </div>
          </div>

          <!-- Pregunta y opciones -->
          <div class="card-body quiz-card__body">
            <p class="quiz-question">{{ preguntaActual.texto }}</p>

            <div class="quiz-options">
              <button
                v-for="(opcion, idx) in preguntaActual.opciones"
                :key="opcion.id"
                class="quiz-option"
                :class="{ 'quiz-option--selected': respuestas[preguntaActual.id] === opcion.id }"
                @click="seleccionarOpcion(preguntaActual.id, opcion.id)"
              >
                <span class="quiz-option__letra">{{ letraOpcion(idx) }}</span>
                <span class="quiz-option__texto">{{ opcion.texto }}</span>
                <span class="quiz-option__check">
                  <i class="fa fa-check"></i>
                </span>
              </button>
            </div>

            <p v-if="errorPreguntaActual" class="quiz-error">
              <i class="fa fa-exclamation-circle me-1"></i>Selecciona una opción para continuar.
            </p>
          </div>

          <!-- Navegación -->
          <div class="card-footer quiz-card__footer">
            <button
              class="btn btn-outline-secondary"
              :disabled="currentIndex === 0"
              @click="anterior()"
            >
              ← Anterior
            </button>
            <button
              class="btn btn-primary"
              @click="siguiente()"
            >
              {{ esUltimaPregunta ? 'Revisar respuestas →' : 'Siguiente →' }}
            </button>
          </div>
        </div>

        <!-- ═══════════  REVISIÓN  ═══════════ -->
        <div v-else-if="enRevision" class="card quiz-card">

          <div class="card-header quiz-card__header">
            <h5 class="mb-1">Revisa tus respuestas</h5>
            <p class="mb-0 quiz-card__subtitle">
              Verifica que todo esté correcto antes de evaluar. Haz clic en cualquier respuesta para editarla.
            </p>
          </div>

          <div class="card-body p-0">
            <div
              v-for="(pregunta, idx) in preguntas"
              :key="pregunta.id"
              class="quiz-review-item"
              :class="{ 'quiz-review-item--sin-respuesta': !respuestas[pregunta.id] }"
              @click="editarRespuesta(idx)"
            >
              <div class="quiz-review-item__num">{{ idx + 1 }}</div>
              <div class="quiz-review-item__content">
                <p class="quiz-review-item__pregunta">{{ pregunta.texto }}</p>
                <p class="quiz-review-item__respuesta">
                  <span v-if="respuestas[pregunta.id]">
                    <i class="fa fa-check-circle text-success me-1"></i>
                    {{ labelOpcion(pregunta, respuestas[pregunta.id]) }}
                  </span>
                  <span v-else class="text-danger">
                    <i class="fa fa-exclamation-circle me-1"></i>Sin respuesta — haz clic para responder
                  </span>
                </p>
              </div>
              <i class="fa fa-pencil quiz-review-item__edit"></i>
            </div>
          </div>

          <div v-if="errorEnvio" class="px-4 pt-3">
            <div class="alert alert-danger py-2 small mb-0">{{ errorEnvio }}</div>
          </div>

          <div class="card-footer quiz-card__footer">
            <button class="btn btn-outline-secondary" @click="volverDeRevision()">
              ← Volver
            </button>
            <button
              class="btn btn-primary"
              :disabled="enviando || tieneRespuestasIncompletas"
              @click="enviarRespuestas()"
            >
              <span v-if="enviando" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <i v-else class="fa fa-check me-1"></i>
              {{ respuestaId ? 'Actualizar evaluación' : 'Evaluar' }}
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import api from "@/axios";
import authService from "@/services/authService";

const BASE = "https://rverdeadmin.klatus.systems";

export default {
  name: "DiagnosticoQuiz",

  data() {
    return {
      titulo:               "",
      descripcion:          "",
      preguntas:            [],
      respuestas:           {},
      loading:              false,
      errorCarga:           null,
      enviando:             false,
      errorEnvio:           null,
      userId:               null,
      respuestaId:          null,
      evaluacionPrevia:     null,
      currentIndex:         0,
      enRevision:           false,
      errorPreguntaActual:  false,
      autoAdvanceTimer:     null,
    };
  },

  computed: {
    preguntaActual() {
      return this.preguntas[this.currentIndex] ?? null;
    },
    esUltimaPregunta() {
      return this.currentIndex === this.preguntas.length - 1;
    },
    respondidas() {
      return this.preguntas.filter((p) => this.respuestas[p.id] != null).length;
    },
    progreso() {
      if (!this.preguntas.length) return 0;
      return Math.round((this.respondidas / this.preguntas.length) * 100);
    },
    tieneRespuestasIncompletas() {
      return this.preguntas.some((p) => !this.respuestas[p.id]);
    },
  },

  created() {
    const userData = authService.getUserData();
    this.userId = userData?.id ?? userData?.pk ?? null;
    this.init();
  },

  beforeUnmount() {
    clearTimeout(this.autoAdvanceTimer);
  },

  methods: {
    letraOpcion(idx) {
      return String.fromCharCode(65 + idx);
    },

    labelOpcion(pregunta, opcionId) {
      return pregunta.opciones?.find((o) => o.id === opcionId)?.texto ?? '';
    },

    seleccionarOpcion(pregId, opcionId) {
      this.respuestas = { ...this.respuestas, [pregId]: opcionId };
      this.errorPreguntaActual = false;

      // Auto-avanza a la siguiente pregunta después de 380 ms
      if (!this.esUltimaPregunta) {
        clearTimeout(this.autoAdvanceTimer);
        this.autoAdvanceTimer = setTimeout(() => this.siguiente(true), 380);
      }
    },

    siguiente(autoAvance = false) {
      if (!autoAvance && !this.respuestas[this.preguntaActual?.id]) {
        this.errorPreguntaActual = true;
        return;
      }
      this.errorPreguntaActual = false;
      if (this.esUltimaPregunta) {
        this.enRevision = true;
      } else {
        this.currentIndex++;
      }
    },

    anterior() {
      this.errorPreguntaActual = false;
      clearTimeout(this.autoAdvanceTimer);
      if (this.currentIndex > 0) this.currentIndex--;
    },

    volverDeRevision() {
      this.enRevision = false;
      this.currentIndex = this.preguntas.length - 1;
    },

    editarRespuesta(idx) {
      this.enRevision = false;
      this.currentIndex = idx;
    },

    async init() {
      this.loading = true;
      this.errorCarga = null;
      try {
        const [cuestionarioRes, previaRes] = await Promise.allSettled([
          api.get(`${BASE}/diagnostico/api/cuestionarios/1`),
          this.userId
            ? api.get(`${BASE}/diagnostico/api/usuarios/${this.userId}/respuestas/`)
            : Promise.resolve(null),
        ]);

        if (cuestionarioRes.status === "rejected") throw cuestionarioRes.reason;

        const cuestionario = cuestionarioRes.value.data;
        this.titulo      = cuestionario.titulo ?? "Cuestionario de diagnóstico";
        this.descripcion = cuestionario.descripcion ?? "";
        this.preguntas   = cuestionario.preguntas ?? [];

        this.respuestas = {};
        this.preguntas.forEach((p) => { this.respuestas[p.id] = null; });

        if (previaRes?.status === "fulfilled" && previaRes.value) {
          const lista  = previaRes.value.data?.respuestas ?? [];
          const previa = lista.find((r) => r.cuestionario?.id === 1) ?? lista[0];
          if (previa) {
            this.respuestaId      = previa.respuesta_id;
            this.evaluacionPrevia = {
              respuesta_id: previa.respuesta_id,
              total:        previa.total,
              nivel:        previa.nivel,
            };
            previa.respuestas?.forEach((r) => {
              this.respuestas[r.pregunta_id] = r.opcion_id;
            });
          }
        }
      } catch (err) {
        this.errorCarga = err.response?.data?.detail ?? "Error al cargar el cuestionario.";
      } finally {
        this.loading = false;
      }
    },

    async enviarRespuestas() {
      if (this.tieneRespuestasIncompletas) return;
      this.enviando  = true;
      this.errorEnvio = null;
      try {
        const payload = {
          respuestas: this.preguntas.map((p) => ({
            pregunta_id: p.id,
            opcion_id:   this.respuestas[p.id],
          })),
        };

        let data;
        if (this.respuestaId && this.userId) {
          ({ data } = await api.put(
            `${BASE}/diagnostico/api/usuarios/${this.userId}/respuestas/${this.respuestaId}/`,
            payload
          ));
        } else {
          ({ data } = await api.post(
            `${BASE}/diagnostico/api/cuestionarios/1/responder/`,
            payload
          ));
        }

        this.$router.push({
          name: "diagnosticoResultado",
          params: { resultado: JSON.stringify(data) },
        });
      } catch (err) {
        this.errorEnvio =
          err.response?.data?.detail ?? "Error al enviar las respuestas. Intenta de nuevo.";
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Card base ─────────────────────────────────────────────────────────── */
.quiz-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 28px rgba(9, 55, 124, 0.10);
  overflow: hidden;
}

/* ── Header ────────────────────────────────────────────────────────────── */
.quiz-card__header {
  background: linear-gradient(135deg, #09377c 0%, #1e59a2 100%);
  padding: 1.5rem 2rem 1.25rem;
  border-bottom: none;
}
.quiz-card__step      { color: rgba(255,255,255,.75); font-size: .9rem; }
.quiz-card__step strong { color: #fff; font-size: 1.1rem; }
.quiz-card__answered  { color: rgba(255,255,255,.65); font-size: .82rem; }
.quiz-card__subtitle  { color: rgba(255,255,255,.72); font-size: .88rem; }
.quiz-card__header h5 { color: #fff; font-weight: 700; }

/* ── Progress bar ──────────────────────────────────────────────────────── */
.quiz-progress {
  height: 6px;
  background: rgba(255,255,255,.2);
  border-radius: 3px;
  overflow: hidden;
}
.quiz-progress__bar {
  height: 100%;
  background: #66bb6a;
  border-radius: 3px;
  transition: width .4s ease;
}

/* ── Body ──────────────────────────────────────────────────────────────── */
.quiz-card__body  { padding: 2rem 2rem 1.5rem; }
.quiz-question {
  font-size: 1.15rem;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.65;
  margin-bottom: 1.75rem;
}

/* ── Option buttons ────────────────────────────────────────────────────── */
.quiz-options { display: flex; flex-direction: column; gap: .65rem; }

.quiz-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: .9rem 1.25rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #1a1a2e;
  font-size: .93rem;
  text-align: left;
  cursor: pointer;
  transition: border-color .15s, background .15s, color .15s, box-shadow .15s;
}
.quiz-option:hover {
  border-color: #09377c;
  background: #f0f5ff;
  box-shadow: 0 2px 8px rgba(9,55,124,.08);
}
.quiz-option--selected {
  border-color: #09377c;
  background: #09377c;
  color: #fff;
  box-shadow: 0 3px 12px rgba(9,55,124,.25);
}

.quiz-option__letra {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: .78rem;
  background: rgba(9,55,124,.1);
  color: #09377c;
  transition: background .15s, color .15s;
}
.quiz-option--selected .quiz-option__letra {
  background: rgba(255,255,255,.2);
  color: #fff;
}

.quiz-option__texto { flex-grow: 1; line-height: 1.4; }

.quiz-option__check {
  flex-shrink: 0;
  opacity: 0;
  color: #fff;
  font-size: .85rem;
  transition: opacity .15s;
}
.quiz-option--selected .quiz-option__check { opacity: 1; }

/* ── Error inline ──────────────────────────────────────────────────────── */
.quiz-error {
  color: #c62828;
  font-size: .85rem;
  margin: .75rem 0 0;
}

/* ── Footer navigation ─────────────────────────────────────────────────── */
.quiz-card__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 2rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

/* ── Review items ──────────────────────────────────────────────────────── */
.quiz-review-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.75rem;
  border-bottom: 1px solid #f0f2f5;
  cursor: pointer;
  transition: background .15s;
}
.quiz-review-item:last-child { border-bottom: none; }
.quiz-review-item:hover { background: #f8fafc; }
.quiz-review-item--sin-respuesta { background: #fff8f8; }
.quiz-review-item--sin-respuesta:hover { background: #fdecea; }

.quiz-review-item__num {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #09377c;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: .75rem;
  font-weight: 700;
  margin-top: .1rem;
}
.quiz-review-item--sin-respuesta .quiz-review-item__num { background: #c62828; }

.quiz-review-item__content { flex-grow: 1; }
.quiz-review-item__pregunta {
  font-size: .88rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 .25rem;
  line-height: 1.4;
}
.quiz-review-item__respuesta {
  font-size: .83rem;
  color: #555;
  margin: 0;
}

.quiz-review-item__edit {
  flex-shrink: 0;
  color: #aaa;
  font-size: .85rem;
  margin-top: .3rem;
  transition: color .15s;
}
.quiz-review-item:hover .quiz-review-item__edit { color: #09377c; }

/* ── Evaluación previa ─────────────────────────────────────────────────── */
.quiz-previa {
  border-left: 4px solid #09377c;
  background: #f0f5ff;
}

/* ── Responsive ────────────────────────────────────────────────────────── */
@media (max-width: 576px) {
  .quiz-card__body   { padding: 1.5rem 1.25rem 1.25rem; }
  .quiz-card__footer { padding: 1rem 1.25rem; }
  .quiz-review-item  { padding: .85rem 1.25rem; }
  .quiz-question     { font-size: 1rem; }
}
</style>